import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import storage from "redux-persist/es/storage";
import { ExportNotifyResponse } from "../services/ExportV2/exportV2API.types";
import {
  MODULE_BIRTHDAYS_AR,
  MODULE_CBR_AR,
  MODULE_CUSTOMER,
  MODULE_CVR_AR,
  MODULE_EDLOS,
  MODULE_GOALS_AR,
  MODULE_LOS,
  MODULE_NON_BONUSABLE_BV_AR,
  MODULE_ORDER,
  MODULE_PROVISIONAL_AR,
  MODULE_RENEWALS,
  MODULE_WATCHLIST,
  MODULE_WHOS_CLOSE,
  MODULE_WHOSNEW,
} from "../common/constants/export-constants";

export type ExportModules = {
  [key: string]: ExportNotifyResponse;
  [MODULE_CUSTOMER]: ExportNotifyResponse;
  [MODULE_ORDER]: ExportNotifyResponse;
  [MODULE_LOS]: ExportNotifyResponse;
  [MODULE_EDLOS]: ExportNotifyResponse;
  [MODULE_BIRTHDAYS_AR]: ExportNotifyResponse;
  [MODULE_GOALS_AR]: ExportNotifyResponse;
  [MODULE_PROVISIONAL_AR]: ExportNotifyResponse;
  [MODULE_CBR_AR]: ExportNotifyResponse;
  [MODULE_CVR_AR]: ExportNotifyResponse;
  [MODULE_WATCHLIST]: ExportNotifyResponse;
  [MODULE_WHOSNEW]: ExportNotifyResponse;
  [MODULE_WHOS_CLOSE]: ExportNotifyResponse;
  [MODULE_NON_BONUSABLE_BV_AR]: ExportNotifyResponse;
  [MODULE_RENEWALS]: ExportNotifyResponse;
};

export type Exports = {
  modules: ExportModules;
  dismissedExportIds: string[];
};

const defaultExport: ExportNotifyResponse = {
  Exports: [],
  ExportCount: 0,
  VisibleExportRequestCount: 0,
};

const defaultModules: ExportModules = {
  [MODULE_CUSTOMER]: defaultExport,
  [MODULE_ORDER]: defaultExport,
  [MODULE_LOS]: defaultExport,
  [MODULE_BIRTHDAYS_AR]: defaultExport,
  [MODULE_GOALS_AR]: defaultExport,
  [MODULE_PROVISIONAL_AR]: defaultExport,
  [MODULE_CBR_AR]: defaultExport,
  [MODULE_CVR_AR]: defaultExport,
  [MODULE_WATCHLIST]: defaultExport,
  [MODULE_WHOSNEW]: defaultExport,
  [MODULE_WHOS_CLOSE]: defaultExport,
  [MODULE_NON_BONUSABLE_BV_AR]: defaultExport,
  [MODULE_RENEWALS]: defaultExport,
};

const { actions, reducer } = createSlice({
  name: "export",
  initialState: {
    modules: defaultModules,
    dismissedExportIds: [],
  } as Exports,
  reducers: {
    updateExport: (state, action: PayloadAction<{ moduleId: string; data: ExportNotifyResponse }>) => {
      state.modules[action.payload.moduleId] = action.payload.data;
    },
    deleteExportId: (state, action: PayloadAction<{ moduleId: string; exportId: string }>) => {
      const filteredExports = state.modules[action.payload.moduleId].Exports.filter(
        (e) => e.ExportID !== action.payload.exportId,
      );
      state.modules[action.payload.moduleId] = {
        ...state.modules[action.payload.moduleId],
        Exports: filteredExports,
        ExportCount: filteredExports.length,
        VisibleExportRequestCount: filteredExports.length,
      };
    },
    clearExportModule: (state, action: PayloadAction<{ moduleId: string }>) => {
      state.modules[action.payload.moduleId] = defaultExport;
    },
    addDismissedExportId: (state, action: PayloadAction<string>) => {
      if (!state.dismissedExportIds.includes(action.payload)) {
        state.dismissedExportIds.push(action.payload);
      }
    },
    clearDismissedExportIds: (state) => {
      state.dismissedExportIds = [];
    },
  },
});

const exportsPersistConfig = {
  key: "export",
  storage,
  stateReconciler: autoMergeLevel2,
};

export const { updateExport, deleteExportId, clearExportModule, addDismissedExportId, clearDismissedExportIds } =
  actions;

export default persistReducer<Exports>(exportsPersistConfig, reducer);
